import { LocalDate, type ZoneId } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSet } from "@uidotdev/usehooks";
import axios from "axios";
import { useCallback, useMemo, useState } from "react";
import {
	getSocialPlatforms,
	type PostSuccessDto,
	postLabel,
	type SocialPlatform,
	type SocialStatsDto,
	type TopEngagedPostsDto,
} from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { percentageDifference } from "../helpers/percent-difference";
import {
	getDictionaryFromData,
	sumDictionaryData,
} from "../helpers/social-data-helpers";
import { QueryKeys } from "../LinkGen";

export const useReport = (
	dateRange: Array<LocalDate>,
	platformsArray = getSocialPlatforms(),
) => {
	const [fromDate, setFromDate] = useState<LocalDate>(dateRange[0]);
	const [toDate, setToDate] = useState<LocalDate>(dateRange[1]);
	const platforms = useSet<SocialPlatform>(platformsArray);
	const platformsKey = Array.from(platforms).sort().join(",");
	const queryClient = useQueryClient();
	const { id: companyId } = useCompany();

	const { data, refetch } = useQuery({
		queryKey: [
			...QueryKeys.Report(companyId),
			fromDate.toString(),
			toDate.toString(),
			platformsKey,
		],
		queryFn: async () => {
			// Empty selection must not fall back to "all platforms" on the API
			if (!platformsKey) {
				return {} as SocialStatsDto;
			}
			return (
				await axios.get<SocialStatsDto>(
					`/api/reports?from=${fromDate}&to=${toDate}&platforms=${platformsKey}`,
				)
			).data;
		},
	});

	const dataLength = useMemo(
		() =>
			data ? Object.values(data).reduce((acc, arr) => acc + arr.length, 0) : 0,
		[data],
	);

	const handlePlatformToggleChange = useCallback(
		(value: SocialPlatform) => {
			if (platforms.has(value)) {
				platforms.delete(value);
				return;
			}

			platforms.add(value);
		},
		[platforms],
	);

	const latestValues = useMemo(
		() =>
			Object.values(data ?? {}).map(
				(platform) => platform[platform.length - 1],
			),
		[data],
	);

	const allValues = useMemo(
		() =>
			Object.values(data ?? {})?.reduce((acc, curr) => {
				return acc.concat(curr);
			}, []),
		[data],
	);

	const totalFollowers = useMemo(
		() => latestValues.reduce((acc, val) => acc + (val.followers ?? 0), 0),
		[latestValues],
	);

	const likes = useMemo(
		() => allValues.reduce((acc, val) => acc + (val.likes ?? 0), 0),
		[allValues],
	);
	const comments = useMemo(
		() => allValues.reduce((acc, val) => acc + (val.comments ?? 0), 0),
		[allValues],
	);
	const shares = useMemo(
		() => allValues.reduce((acc, val) => acc + (val.shares ?? 0), 0),
		[allValues],
	);
	const views = useMemo(
		() => allValues.reduce((acc, val) => acc + (val.impressions ?? 0), 0),
		[allValues],
	);

	const dataDict = useMemo(
		() => (data ? getDictionaryFromData(data) : {}),
		[data],
	);
	const dataSummed = useMemo(
		() =>
			sumDictionaryData(dataDict).sort((a, b) =>
				LocalDate.parse(a.date).isAfter(LocalDate.parse(b.date)) ? 1 : -1,
			),
		[dataDict],
	);

	const startingFolowers = useMemo(
		() => dataSummed[0]?.followers ?? 0,
		[dataSummed],
	);
	const followersDiff = useMemo(
		() => totalFollowers - startingFolowers,
		[totalFollowers, startingFolowers],
	);
	const followersGrowth = useMemo(
		() => percentageDifference(totalFollowers, startingFolowers),
		[totalFollowers, startingFolowers],
	);

	const forceRefresh = useMutation({
		mutationFn: async () => {
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.log("[Reports] Starting social stats sync…");
			const { data: result } = await Api.UpdateSocialStatsReport();
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.log("[Reports] Sync response:", result);
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.log("[Reports] X engagement sync:", result?.xEngagement ?? null);
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.log(
				"[Reports] X delta (cards):",
				result?.xEngagement?.delta ?? null,
			);
			return result;
		},
		onSuccess: async (result) => {
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.log("[Reports] Sync finished:", {
				companyId: result?.companyId,
				updatedPlatforms: result?.updatedPlatforms,
				xEngagement: result?.xEngagement,
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.Report(companyId),
			});
			await queryClient.invalidateQueries({
				queryKey: QueryKeys.ReportTopPosts(companyId),
			});
			try {
				const topPosts = await axios.get<TopEngagedPostsDto>(
					"/api/reports/top-posts",
					{
						params: {
							from: fromDate.toString(),
							to: toDate.toString(),
							limit: 10,
							...(platformsKey ? { platforms: platformsKey } : {}),
						},
					},
				);
				// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
				console.log(
					"[Reports] Top posts after sync:",
					topPosts.data.map((p) => ({
						id: p.id,
						platform: p.platform,
						likes: p.likes,
						comments: p.comments,
						shares: p.shares,
						views: p.views,
						total: p.totalEngagement,
						preview: postLabel(p),
					})),
				);
			} catch (e) {
				// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
				console.warn("[Reports] Failed to fetch top posts after sync:", e);
			}
		},
		onError: (error) => {
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.error("[Reports] Sync failed:", error);
		},
	});

	return {
		fromDate,
		toDate,
		platforms,
		data,
		dataLength,
		latestValues,
		allValues,
		startingFolowers,
		totalFollowers,
		likes,
		shares,
		comments,
		views,
		followersDiff,
		followersGrowth,
		dataSummed,
		setFromDate,
		forceRefresh,
		setToDate,
		refetch,
		handlePlatformToggleChange,
	};
};

export const usePostSuccessReport = (
	from: string,
	to: string,
	zone: ZoneId,
) => {
	const { id } = useCompany();
	const { data, refetch } = useQuery({
		queryKey: QueryKeys.ReportPostStatus(id),

		queryFn: async () =>
			(
				await axios.get<PostSuccessDto>("/api/reports/post-success", {
					params: { from, to, zone: zone.toString() },
				})
			).data,
	});

	return { data, refetch };
};

export const useTopEngagedPosts = (
	from: LocalDate,
	to: LocalDate,
	limit = 10,
	platforms: SocialPlatform[] = [],
) => {
	const { id } = useCompany();
	const platformsKey = [...platforms].sort().join(",");
	const { data, refetch, isLoading, error } = useQuery({
		queryKey: [
			...QueryKeys.ReportTopPosts(id),
			from.toString(),
			to.toString(),
			limit,
			platformsKey,
		],
		queryFn: async () => {
			const posts = (
				await axios.get<TopEngagedPostsDto>("/api/reports/top-posts", {
					params: {
						from: from.toString(),
						to: to.toString(),
						limit,
						...(platformsKey ? { platforms: platformsKey } : {}),
					},
				})
			).data;
			// biome-ignore lint/suspicious/noConsole: debug X engagement sync in browser
			console.log(
				"[Reports] Top engaged posts:",
				posts.map((p) => ({
					id: p.id,
					platform: p.platform,
					likes: p.likes,
					comments: p.comments,
					shares: p.shares,
					views: p.views,
					total: p.totalEngagement,
					preview: postLabel(p),
				})),
			);
			return posts;
		},
		enabled: platforms.length > 0,
	});

	return { data, refetch, isLoading, error };
};
